// THE PEDIGREE AUTOSAVE'S SCHEDULER, AND THE HOLD THAT KEEPS A STALE ROW OFF
// THE WIRE.
//
// ── THE SCHEDULER ─────────────────────────────────────────────────────────
// Moved here unchanged from PedigreeChart, where it was built inline, so the
// hold below can be pinned by tests without mounting the chart.
//
// The previous `debounce(onSumbit, 0)` was RECREATED on every render, so each
// instance had its own `timeout` and `clearTimeout` never cancelled a prior
// pending call. With wait=0, every memberRecords change fired its own near-
// immediate save, so rapidly adding members produced a burst of OVERLAPPING
// POST /pedigree-config requests. Each re-sent nodes whose server-assigned
// familyMemberId hadn't come back yet, and the backend minted a FRESH UPN for
// each — so the same node was minted several times and the in-between numbers
// were burned (the skipped UPNs: 9, 10, 11, _12_, 13, …).
//
// One stable controller (a) COALESCES rapid edits into one save and (b) never
// overlaps an in-flight save — edits that land mid-save trigger one re-run
// after it resolves, by which point the saved nodes carry their familyMemberId
// and are no longer re-minted. No cleanup-on-unmount: a trailing save still
// flushes if the user navigates away.
//
// ── THE HOLD ──────────────────────────────────────────────────────────────
// The autosave POSTs every member's WHOLE row from memberRecords, and the
// server $sets each non-proband row wholesale. So a row that another endpoint
// has just changed on the server, and that memberRecords still holds in its old
// shape, is written back over that change by the next save. Two writers need
// this:
//
//   - a Test record's variants pushed into the member's genotypes
//     (POST /genotypes/:user_id/:familyMemberId/variants/push). The server
//     dedupes and ClinVar-enriches them, so the client cannot build the new
//     genotypes itself and has to read them back.
//   - the person-link sync (POST /person-links/sync-secondaries), which writes
//     name, date of birth and sex onto linked records in OTHER families, any
//     of which may be open in another workspace tab
//     (useExternalMemberRowRefresh.js).
//
// A hold says "a row in this chart is known to be stale, and its refresh from
// the server has not landed yet". While any hold is taken, planSaveAttempt
// DEFERS: nothing is sent and the pending status is left as it is, so the edit
// that armed it is still sent — once, from the refreshed rows — when the last
// hold is released. Deferring the whole save rather than dropping the stale row
// from it is deliberate: the POST is also the family's stored topology (the
// server writes the pedigreeconfigs snapshot from the same array), so a row
// left out of it would be a person left out of the pedigree.
//
// Release re-arms the scheduler through the normal debounce rather than firing
// at once. The holder patches memberRecords just before it releases, and the
// save reads the records from a ref that PedigreeChart updates after React
// commits; the debounce is what puts the fire after that commit.
//
// `whenIdle` is the other half, for a writer that is about to change a row on
// the server: a save already in flight was sent with the row as it is now, and
// if the server applied it AFTER the writer's own change it would undo it. So
// such a writer takes its hold FIRST (no new save can start), then waits for
// the in-flight one to settle, and only then sends its own request. The
// server answers a pedigree save only after every row write has finished, so
// "settled" means "applied".

export const AUTOSAVE_DEBOUNCE_MS = 300;

// A hold is released when its task settles. This cap only matters for a
// request that never settles: the clinician's own pending edits would
// otherwise wait behind it for as long as the tab stays open. After it the save
// goes ahead, which can resend the stale row — the defect the hold exists for —
// but only in the case where the server never answered at all.
export const MAX_AUTOSAVE_HOLD_MS = 30000;

/**
 * @param {object} args
 * @param {() => (Promise|void)} args.submit one autosave attempt (onSumbit)
 * @param {number} [args.debounceMs]
 */
export function createAutosaveController({
  submit,
  debounceMs = AUTOSAVE_DEBOUNCE_MS
}) {
  let timer = null;
  let inFlight = false;
  let pending = false;
  let idleWaiters = [];
  const holds = new Set();

  const settleIdle = () => {
    if (inFlight) return;
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  };

  const fire = () => {
    timer = null;
    if (inFlight) {
      pending = true;
      return;
    }
    inFlight = true;
    // Called synchronously, as it always was: the attempt reads the latest
    // records at the moment the timer goes off. A synchronous throw is turned
    // into a rejection so it cannot leave `inFlight` latched for good.
    let attempt;
    try {
      attempt = submit();
    } catch (err) {
      attempt = Promise.reject(err);
    }
    Promise.resolve(attempt)
      .catch(() => {})
      .finally(() => {
        inFlight = false;
        if (pending) {
          pending = false;
          fire();
          return;
        }
        settleIdle();
      });
  };

  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fire, debounceMs);
  };

  const whenIdle = () =>
    inFlight
      ? new Promise((resolve) => idleWaiters.push(resolve))
      : Promise.resolve();

  /**
   * Take a hold. Returns its release, which is idempotent; releasing the last
   * hold re-arms the scheduler.
   */
  const hold = (reason) => {
    const token = { reason };
    holds.add(token);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      holds.delete(token);
      if (holds.size === 0) schedule();
    };
  };

  return {
    schedule,
    whenIdle,
    hold,
    isHeld: () => holds.size > 0
  };
}

/**
 * Run `task` — a write that changes a row on the server, followed by putting
 * the server's answer on that row in memberRecords — with the autosave held
 * for its whole length. The hold is taken before anything else, a save already
 * in flight is allowed to settle before `task` starts, and the hold is released
 * however `task` ends.
 */
export async function runWithAutosaveHeld(
  controller,
  task,
  { reason, maxHoldMs = MAX_AUTOSAVE_HOLD_MS } = {}
) {
  const release = controller.hold(reason);
  const cap = setTimeout(release, maxHoldMs);
  try {
    await controller.whenIdle();
    return await task();
  } finally {
    clearTimeout(cap);
    release();
  }
}
